/**
 * 🟥 CUADRO NEGRO V7 — CiStatusBadge.
 *
 * Badge compacto que muestra el estado CI del último run del branch actual.
 * Auto-refresca cada 30s mientras el componente esté montado.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, HelpCircle, ExternalLink } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const STYLES = {
  passing: { bg: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', label: 'Passing', Icon: CheckCircle2 },
  failing: { bg: 'bg-rose-500/15 text-rose-300 border-rose-500/40', label: 'Failing', Icon: XCircle },
  running: { bg: 'bg-amber-500/15 text-amber-300 border-amber-500/40', label: 'Running', Icon: Loader2 },
  unknown: { bg: 'bg-slate-700/40 text-slate-400 border-slate-700', label: 'No CI', Icon: HelpCircle },
};

export default function CiStatusBadge({ moduleId, branch, isEs = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const params = { module_id: moduleId };
      if (branch) params.branch = branch;
      const res = await axios.get(`${API}/github/ci-status`, { params });
      setData(res.data);
    } catch (_e) {
      // Silencioso: si no hay github_source o repo, badge queda en 'unknown'.
      setData({ status: 'unknown', last_run: null, branch: branch || '' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(() => { if (!cancelled) fetchStatus(); }, 0);
    const interval = setInterval(() => { if (!cancelled) fetchStatus(); }, 30_000);
    return () => { cancelled = true; clearTimeout(id); clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, branch]);

  if (loading && !data) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-md bg-slate-800 text-slate-500 border border-slate-700">
        <Loader2 className="w-3 h-3 animate-spin" />
        CI
      </span>
    );
  }

  const status = data?.status || 'unknown';
  const meta = STYLES[status] || STYLES.unknown;
  const Icon = meta.Icon;
  const run = data?.last_run;
  const title = run
    ? `${meta.label} · ${run.name || 'CI'}\n${run.commit_message || ''}\n${run.commit_sha?.slice(0, 7) || ''} · ${run.actor || ''}`
    : (isEs ? 'Sin runs detectados' : 'No runs detected');

  const content = (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-md border ${meta.bg}`}
      title={title}
      data-testid="ci-status-badge"
    >
      <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      <span>{meta.label}</span>
      {run?.html_url && <ExternalLink className="w-2.5 h-2.5 opacity-60" />}
    </span>
  );

  if (run?.html_url) {
    return (
      <a href={run.html_url} target="_blank" rel="noreferrer" className="no-underline">
        {content}
      </a>
    );
  }
  return content;
}
